import {
  ensureDataLayer,
  isTestOrder,
  pushDirectRawGa4Event,
  pushGa4Event,
  withTestOrderFlag,
  type AnalyticsProperties,
} from "@/lib/analytics-ga4";
import {
  isServerCanonicalEvent,
  sendOwnedAnalyticsEvent,
  sendServerAnalyticsEvent,
} from "@/lib/analytics-server";
import { isBrowserTelemetrySuppressed } from "@/lib/sensitive-route-policy";

declare global {
  interface Window {
    /** Set only by the production Playwright monitor before application code runs. */
    __ishSyntheticMonitor?: boolean;
  }
}

function syntheticTrafficMarker(): { traffic_class: "synthetic" } | Record<string, never> {
  return window.__ishSyntheticMonitor === true ? { traffic_class: "synthetic" } : {};
}

function detectDeviceType(): "Mobile" | "Tablet" | "Desktop" | "Unknown" {
  if (typeof navigator === "undefined") return "Unknown";
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet|PlayBook|Silk/.test(ua)) return "Tablet";
  if (/Mobi|Android|iPhone|iPod|Opera Mini|IEMobile|webOS|BlackBerry/.test(ua)) return "Mobile";
  return "Desktop";
}

export function trackEvent(name: string, properties?: AnalyticsProperties) {
  if (typeof window === "undefined") return;
  if (isBrowserTelemetrySuppressed()) return;
  const serverCanonical = isServerCanonicalEvent(name);
  const eventProperties = {
    event_source: "client" as const,
    $device_type: detectDeviceType(),
    ...(withTestOrderFlag(properties) ?? {}),
    // Applied last so a monitor can never leak into human KPIs through a caller
    // that supplied another traffic_class. Funnel marts promote this marker to
    // the whole browser client before calculating business rates.
    ...syntheticTrafficMarker(),
  };
  const testOrder = isTestOrder(eventProperties);

  ensureDataLayer().push({ event: name, ...eventProperties });

  if (!testOrder) {
    if (serverCanonical) {
      sendServerAnalyticsEvent(name, eventProperties);
    } else {
      pushDirectRawGa4Event(name, eventProperties);
      pushGa4Event(name, eventProperties);
    }
  }

  if (testOrder || serverCanonical) return;
  sendOwnedAnalyticsEvent(name, eventProperties);
}

export function trackScreenView(properties: {
  screen: string;
  screen_id?: string;
  route?: string;
  platform?: "web";
}) {
  trackEvent("screen_view", {
    ...properties,
    screen_id: properties.screen_id ?? properties.screen,
    platform: properties.platform ?? "web",
  });
}

export function trackOwnedAnalyticsOnly(name: string, properties?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  sendOwnedAnalyticsEvent(name, { ...(properties ?? {}), ...syntheticTrafficMarker() });
}
